import {flatMap, uniq} from 'es-toolkit'
import invariant from 'invariant'
import {useNavigate} from 'react-router'

import {useAppDispatch, useAppSelector} from '../../hooks/redux'
import {fatalError} from '../../redux/features/core'
import {openConfirmDeleteModal} from '../../redux/features/modal'

import useAsync from '../../../../commons/src/hooks/use-async'
import useSelectedIds from '../../hooks/use-selected-ids'
import useSortAttributes, {SortDefinition} from '../../hooks/use-sort-attributes'
import * as dynamicProductionTimeApi from '../../services/api/dynamic-production-time'
import {
  goToCreateDynamicProductionTime,
  goToEditDynamicProductionTime,
  goToProviderInfos,
} from '../../services/navigation'

import {
  DynamicProductionTime,
  DynamicProductionTimeId,
  DynamicProductionTimeWithLinkedProviderInfos,
} from '../../types'

import deleteIcon from '../../../../commons/src/assets/icons/delete.svg'
import editIcon from '../../../../commons/src/assets/icons/edit.svg'
import tableSearchIcon from '../../../../commons/src/assets/icons/table-search.svg'

import ActionBar from '../../../../commons/src/components/action-bar'
import Button from '../../../../commons/src/components/button'
import CheckboxField from '../../../../commons/src/components/checkbox-field'
import Headline from '../../../../commons/src/components/headline'
import Icon from '../../../../commons/src/components/icon'
import LinkButton from '../../../../commons/src/components/link-button'
import ActionHeaderLayout from '../../components/action-header-layout'
import ActionHeader from '../../components/action-header-layout/action-header'
import Container from '../../components/container'
import PageContentLayout from '../../components/page-content-layout'
import PageHeader from '../../components/page-content-layout/PageHeader'
import StaggeredValues from '../../components/staggered-values/StaggeredValues'
import Table from '../../components/table'
import TableCell from '../../components/table/TableCell'
import TableHeaderCell from '../../components/table/TableHeaderCell'
import TableRow from '../../components/table/TableRow'

import PagePartial from '../page-partial'

type TableRowContent = DynamicProductionTimeWithLinkedProviderInfos

const SORT_ATTRIBUTES: SortDefinition<TableRowContent> = {
  name: {
    asc: ({name: a}, {name: b}) => a.localeCompare(b),
    desc: ({name: a}, {name: b}) => b.localeCompare(a),
  },
  increaseSteps: {
    asc: ({increaseSteps: a}, {increaseSteps: b}) => a - b,
    desc: ({increaseSteps: a}, {increaseSteps: b}) => b - a,
  },
  maxIncrease: {
    asc: ({maxIncrease: a}, {maxIncrease: b}) => a - b,
    desc: ({maxIncrease: a}, {maxIncrease: b}) => b - a,
  },
  linkedConfigs: {
    asc: ({providerInfos: a}, {providerInfos: b}) => a.length - b.length,
    desc: ({providerInfos: a}, {providerInfos: b}) => b.length - a.length,
  },
}

const getLinkedConfigIds = (
  productionTimeOptions: DynamicProductionTimeWithLinkedProviderInfos[],
  selectedIds: DynamicProductionTimeId[]
) => {
  const selectedDynamicProductionTimes = productionTimeOptions.filter(({id}) =>
    selectedIds.includes(id)
  )
  return uniq(
    flatMap(selectedDynamicProductionTimes, item =>
      item.providerInfos.map(providerInfo => providerInfo.id)
    )
  )
}

const DynamicProductionTimesPage = () => {
  const dispatch = useAppDispatch()
  const provider = useAppSelector(state => state.core.provider)
  invariant(provider, 'Provider is missing.')

  const navigate = useNavigate()

  const {
    isLoading,
    data: dynamicProductionTimes = [],
    refresh,
  } = useAsync(
    () => dynamicProductionTimeApi.getByProviderWithLinkedProviderInfo(provider.id),
    [],
    {
      onError: error => dispatch(fatalError({error})),
      defaultLoading: true,
    }
  )

  const {toggleId, toggleAll, isSelected, isAnySelected, selectedIds} =
    useSelectedIds<DynamicProductionTimeId>(() => dynamicProductionTimes.map(item => item.id))

  const tableRows: TableRowContent[] = dynamicProductionTimes.map(item => ({
    ...item,
    name: item.name || '-',
  }))

  const {
    sortedItems: sortedTableRows,
    sortAttribute,
    sortOrder,
    toggleSortBy,
  } = useSortAttributes(tableRows, SORT_ATTRIBUTES, 'name')

  const renderQuantityThresholds = (
    quantityThresholds: DynamicProductionTime['quantityThresholds']
  ) => {
    return (
      <StaggeredValues
        divider="->"
        values={quantityThresholds.map(({axisSize, qtyThreshold}) => ({
          left: axisSize,
          right: qtyThreshold,
        }))}
      />
    )
  }

  const renderRow = (tableRow: TableRowContent) => (
    <TableRow key={tableRow.id}>
      <TableCell>
        <ActionBar>
          <CheckboxField checked={isSelected(tableRow.id)} onChange={() => toggleId(tableRow.id)} />
        </ActionBar>
      </TableCell>
      <TableCell strong>{tableRow.name}</TableCell>
      <TableCell>{tableRow.increaseSteps}</TableCell>
      <TableCell>{tableRow.maxIncrease}</TableCell>
      <TableCell>{renderQuantityThresholds(tableRow.quantityThresholds)}</TableCell>
      <TableCell error={tableRow.providerInfos.length === 0}>
        {tableRow.providerInfos.length > 0 ? (
          <LinkButton
            icon={<Icon source={tableSearchIcon} />}
            iconAlign="right"
            onClick={() =>
              goToProviderInfos(navigate, {
                initialFilters: {
                  dynamicProductionTime: [tableRow.id],
                },
              })
            }
          >
            <strong>{tableRow.providerInfos.length}</strong>
          </LinkButton>
        ) : (
          <strong>{tableRow.providerInfos.length}</strong>
        )}
      </TableCell>
    </TableRow>
  )

  const linkedConfigsCount = getLinkedConfigIds(dynamicProductionTimes, selectedIds).length

  return (
    <PagePartial>
      <PageContentLayout
        header={
          <PageHeader
            headline={<Headline size="l" label="Dynamic production time" />}
            actions={
              <Button onClick={() => goToCreateDynamicProductionTime(navigate)}>
                Create new entry
              </Button>
            }
          />
        }
      >
        <Container>
          <ActionHeaderLayout
            header={
              <ActionHeader
                left={
                  <LinkButton onClick={() => toggleAll()}>
                    {isAnySelected() ? 'Deselect all' : 'Select all'}
                  </LinkButton>
                }
                right={
                  <>
                    <LinkButton
                      size="200"
                      icon={<Icon source={editIcon} title="Edit" />}
                      disabled={selectedIds.length === 0}
                      onClick={() =>
                        goToEditDynamicProductionTime(navigate, {
                          dynamicProductionTimes: dynamicProductionTimes.filter(({id}) =>
                            selectedIds.includes(id)
                          ),
                        })
                      }
                    />
                    <LinkButton
                      size="200"
                      icon={<Icon source={deleteIcon} title="Delete" />}
                      disabled={selectedIds.length === 0}
                      onClick={() => {
                        dispatch(
                          openConfirmDeleteModal({
                            itemsToDeleteCount: selectedIds.length,
                            onConfirm: () => {
                              dynamicProductionTimeApi
                                .removeMultiple(selectedIds)
                                .catch(fatalError)
                                .then(refresh)
                            },
                            linkedConfigsCount,
                          })
                        )
                      }}
                    />
                  </>
                }
              />
            }
          >
            <Table
              isLoading={isLoading}
              empty="No dynamic production time has been added yet"
              header={
                <TableRow>
                  <TableHeaderCell width="28px" />
                  <TableHeaderCell
                    width={2}
                    sort={sortAttribute === 'name' && sortOrder ? sortOrder : undefined}
                    onClick={() => toggleSortBy('name')}
                  >
                    Name
                  </TableHeaderCell>
                  <TableHeaderCell
                    width={2}
                    sort={sortAttribute === 'increaseSteps' && sortOrder ? sortOrder : undefined}
                    onClick={() => toggleSortBy('increaseSteps')}
                  >
                    Increase steps
                  </TableHeaderCell>
                  <TableHeaderCell
                    width={2}
                    sort={sortAttribute === 'maxIncrease' && sortOrder ? sortOrder : undefined}
                    onClick={() => toggleSortBy('maxIncrease')}
                  >
                    Max increase steps
                  </TableHeaderCell>
                  <TableHeaderCell width={4}>Quantity Thresholds</TableHeaderCell>
                  <TableHeaderCell
                    width={1}
                    align="right"
                    sort={sortAttribute === 'linkedConfigs' && sortOrder ? sortOrder : undefined}
                    onClick={() => toggleSortBy('linkedConfigs')}
                  >
                    Linked materials
                  </TableHeaderCell>
                </TableRow>
              }
            >
              {sortedTableRows.map(renderRow)}
            </Table>
          </ActionHeaderLayout>
        </Container>
      </PageContentLayout>
    </PagePartial>
  )
}

export default DynamicProductionTimesPage
